import { SEO_SITE, SEO_KO } from '@src/configs/seo.config'
import { SeasonProductType } from '@schemas'

/**
 * schema.org 구조화 데이터 생성기.
 *
 * 검색 결과의 리치 스니펫(가격·재고·평점)과 AI 답변 인용은 둘 다 이걸 읽는다.
 * 예전에는 index.html 에 `OnlineStore` 하나만 있었고 제품 정보가 전혀 없었다.
 */

export interface ProductJsonLdParamsType {
	name: string
	description: string
	/** 라우트 경로. `/products/kdm6` */
	path: string
	image: string | string[]
	/** 시즌 상품 정보. 없으면 offers 를 붙이지 않는다 (출시 전 제품) */
	product?: SeasonProductType
	/** 출시 전이라 가격이 없을 때 PreOrder 로 표시 */
	preOrder?: boolean
	sku?: string
}

/**
 * 재고 상태를 schema.org availability 로 옮긴다.
 *
 * SeasonProductType.status: not_start | expired | inactive | sold_out | active
 */
const toAvailability = (product: SeasonProductType): string => {
	if (product.soldOutYn === 1 || product.status === 'sold_out') {
		return 'https://schema.org/SoldOut'
	}

	if (product.status === 'not_start' || product.preorderYn === 1) {
		return 'https://schema.org/PreOrder'
	}

	if (product.status === 'active') {
		return 'https://schema.org/InStock'
	}

	return 'https://schema.org/OutOfStock'
}

export const buildProductJsonLd = (params: ProductJsonLdParamsType): object => {
	const url = `${SEO_SITE.origin}${params.path}`

	const res: { [key: string]: any } = {
		'@context': 'https://schema.org',
		'@type': 'Product',
		name: params.name,
		description: params.description,
		url: url,
		image: params.image,
		brand: {
			'@type': 'Brand',
			name: SEO_SITE.siteName,
			alternateName: SEO_KO.brand,
		},
	}

	if (params.sku !== undefined && params.sku !== '') {
		res.sku = params.sku

		/*
			한국어 표기 (2026-09-21). 코드로 찾으므로 호출부는 손대지 않는다.
			음역이 없는 제품은 표에 없고, 그러면 alternateName 도 붙지 않는다.
		*/
		const koName = SEO_KO.products[params.sku.toUpperCase()]
		if (koName !== undefined) {
			res.alternateName = koName
		}
	}

	if (params.preOrder === true) {
		// 출시 전 — 가격을 확정할 수 없으므로 재고 상태만 알린다.
		res.offers = {
			'@type': 'Offer',
			url: url,
			availability: 'https://schema.org/PreOrder',
			priceCurrency: 'USD',
		}

		return res
	}

	// 가격이 0 이면 시즌 상품이 아직 안 실렸다는 뜻이라 offers 를 붙이지 않는다.
	// 잘못된 가격을 구조화 데이터로 내보내는 것보다 없는 편이 낫다.
	if (params.product !== undefined && params.product.seasonUsd > 0) {
		/*
			판매가 = seasonUsd. `seasonDiscountUsd` 를 빼지 않는다 — 그 값은 상점·결제 어디서도
			차감되지 않고(Shop.page 의 취소선 스위치일 뿐) 결제 금액은 시즌단가 그대로다.
			빼면 검색 결과의 가격과 실제 결제 가격이 갈린다. 제품 페이지의 보이는 값과도 같아야
			리치 결과에 산다 (PRICING §4).
		*/
		const price = params.product.seasonUsd

		res.offers = {
			'@type': 'Offer',
			url: url,
			priceCurrency: 'USD',
			price: price.toFixed(2),
			availability: toAvailability(params.product),
			seller: {
				'@type': 'Organization',
				name: SEO_SITE.siteName,
			},
		}

		if (params.product.seasonEndDate !== '') {
			res.offers.priceValidUntil = params.product.seasonEndDate.slice(0, 10)
		}
	}

	return res
}

/** 조직 엔티티 id. 홈의 `OnlineStore` · `/about` 의 `Organization` · 소프트웨어 `publisher` 가 같은 값을 쓴다 */
const ORGANIZATION_ID = `${SEO_SITE.origin}/#organization`

/** 같은 회사의 다른 공식 채널. 늘리면 두 엔티티에 함께 들어간다 */
/**
 * 라인 브랜드. 조직 엔티티에 붙여 `Koinos`·`Kedem` 과 그 한국어 음역을 같은 회사의 것으로 묶는다.
 * 제품 JSON-LD 의 `brand` 는 Judahs 하나로 두고, 라인은 여기서만 선언한다.
 */
const ORGANIZATION_BRANDS = SEO_KO.lines.map(aLine => ({
	'@type': 'Brand',
	name: aLine.name,
	alternateName: aLine.ko,
}))

const ORGANIZATION_SAME_AS = [
	'https://www.youtube.com/@judahs-io',
	'https://www.instagram.com/judahs_social',
]

/**
 * 사이트 전체 조직 정보. 홈에 넣는다.
 *
 * `OnlineStore` 는 schema.org 에서 `Organization` 의 하위 타입이라, `/about` 과 같은 `@id` 를 주면
 * 크롤러·AI 가 두 페이지의 회사를 한 실체로 합친다 (SEO_PLAN §9.8).
 *
 * 예전에는 index.html 에 정적으로 박혀 있었다. 프리렌더가 이걸 정적 HTML 로
 * 만들어주므로 양쪽에 두면 같은 엔티티가 두 번 나온다.
 */
export const buildOnlineStoreJsonLd = (): object => ({
	'@context': 'https://schema.org',
	'@type': 'OnlineStore',
	'@id': ORGANIZATION_ID,
	name: SEO_SITE.siteName,
	alternateName: SEO_KO.brand,
	brand: ORGANIZATION_BRANDS,
	url: SEO_SITE.origin,
	logo: SEO_SITE.defaultOgImage,
	description:
		'Builder of MIDI foot controllers and programmable pedal switchers, also called loop switchers, for guitar pedalboards.',
	sameAs: ORGANIZATION_SAME_AS,
	contactPoint: {
		'@type': 'ContactPoint',
		contactType: 'customer service',
		email: 'cs@judahs.io',
		availableLanguage: ['English', 'Korean'],
	},
})

/**
 * 회사 정보 — `/about` 에 넣는다 (SEO_PLAN §4: 브랜드 검색 수신용).
 *
 * 홈의 `OnlineStore` 와 같은 엔티티를 가리키되(`@id` 동일), 여기서는 법인·주소를 붙인다.
 * 우편번호는 아직 확인 전이라(judahsWeb/PENDING_CHANGES 사용자 확인 대기 1) 넣지 않는다 —
 * 구조화 데이터에 틀린 값을 넣느니 비워 둔다.
 */
export const buildOrganizationJsonLd = (): object => ({
	'@context': 'https://schema.org',
	'@type': 'Organization',
	'@id': ORGANIZATION_ID,
	name: SEO_SITE.siteName,
	alternateName: SEO_KO.brand,
	brand: ORGANIZATION_BRANDS,
	url: SEO_SITE.origin,
	logo: SEO_SITE.defaultOgImage,
	description:
		'Builder of MIDI foot controllers and programmable pedal switchers, also called loop switchers, for guitar pedalboards.',
	sameAs: ORGANIZATION_SAME_AS,
	address: {
		'@type': 'PostalAddress',
		streetAddress: 'Unit 304, 1225 Gonghang-ro, Gangseo-gu',
		addressLocality: 'Busan',
		postalCode: '46703',
		addressCountry: 'KR',
	},
	email: 'cs@judahs.io',
	telephone: '+82-51-791-1206',
})

export interface ItemListEntryType {
	name: string
	path: string
}

/** 라인업 목록. /shop 에 넣는다. */
export const buildItemListJsonLd = (entries: ItemListEntryType[]): object => ({
	'@context': 'https://schema.org',
	'@type': 'ItemList',
	itemListElement: entries.map((anEntry, idx) => ({
		'@type': 'ListItem',
		position: idx + 1,
		name: anEntry.name,
		url: `${SEO_SITE.origin}${anEntry.path}`,
	})),
})

export interface BreadcrumbEntryType {
	name: string
	path: string
}

export const buildBreadcrumbJsonLd = (entries: BreadcrumbEntryType[]): object => ({
	'@context': 'https://schema.org',
	'@type': 'BreadcrumbList',
	itemListElement: entries.map((anEntry, idx) => ({
		'@type': 'ListItem',
		position: idx + 1,
		name: anEntry.name,
		item: `${SEO_SITE.origin}${anEntry.path}`,
	})),
})

export interface FaqEntryType {
	question: string
	answer: string
}

/**
 * FAQPage. `/faq` 에 넣는다 (SITEMAP_PLAN §23.6).
 *
 * 답변은 화면에 그대로 보이는 문장이어야 한다 — 구글은 화면에 없는 답을 마크업으로만 내면
 * 리치 결과에서 뺀다. `faq.config.ts` 의 문단을 이어 붙여 쓰는 이유다.
 */
export const buildFaqJsonLd = (entries: FaqEntryType[]): object => ({
	'@context': 'https://schema.org',
	'@type': 'FAQPage',
	mainEntity: entries.map(anEntry => ({
		'@type': 'Question',
		name: anEntry.question,
		acceptedAnswer: {
			'@type': 'Answer',
			text: anEntry.answer,
		},
	})),
})

export interface SoftwareApplicationJsonLdParamsType {
	name: string
	description: string
	/** 라우트 경로. `/software/judahs-studio` */
	path: string
	image?: string
	/** `macOS 13 Ventura or later, Windows 10 (64-bit) or 11` — 화면의 Requirements 그대로 */
	operatingSystem: string
	/** 화면의 "What it does" 불릿 그대로 */
	featureList: string[]
}

/**
 * SoftwareApplication. `/software/{slug}` 에 넣는다 (SEO_PLAN §9.7).
 *
 * 가격 0 은 화면 문구("Judahs Studio costs nothing")와 같은 사실이다. 버전은 넣지 않는다 —
 * 프리렌더가 그 시점 값을 박제해서 새 빌드가 나오는 순간 틀린다(DEPLOY §3.1.1 가격 박제와 같은 문제).
 */
export const buildSoftwareApplicationJsonLd = (
	params: SoftwareApplicationJsonLdParamsType,
): object => {
	const res: { [key: string]: any } = {
		'@context': 'https://schema.org',
		'@type': 'SoftwareApplication',
		name: params.name,
		description: params.description,
		url: `${SEO_SITE.origin}${params.path}`,
		applicationCategory: 'MultimediaApplication',
		operatingSystem: params.operatingSystem,
		featureList: params.featureList,
		offers: {
			'@type': 'Offer',
			price: '0',
			priceCurrency: 'USD',
		},
		publisher: {
			'@type': 'Organization',
			'@id': ORGANIZATION_ID,
			name: SEO_SITE.siteName,
			url: SEO_SITE.origin,
		},
	}

	if (params.image !== undefined) {
		res.image = params.image
	}

	return res
}

export interface CollectionPageJsonLdParamsType {
	name: string
	description: string
	path: string
	/** 이 목록이 무엇에 대한 것인가 — 제품 정본 주소 */
	about: ItemListEntryType[]
	/** 목록 항목. 페이지 안 앵커(`/works-with#strymon`)도 된다 */
	items: ItemListEntryType[]
}

/**
 * CollectionPage + ItemList. `/works-with` 에 넣는다 (SEO_PLAN §9.7).
 *
 * 항목은 제조사 단위다. 장비 수백 개를 다 넣으면 JSON-LD 가 본문보다 커지고, 장비 이름은 이미 본문 칩에 있다.
 */
export const buildCollectionPageJsonLd = (
	params: CollectionPageJsonLdParamsType,
): object => ({
	'@context': 'https://schema.org',
	'@type': 'CollectionPage',
	name: params.name,
	description: params.description,
	url: `${SEO_SITE.origin}${params.path}`,
	about: params.about.map(anEntry => ({
		'@type': 'Product',
		name: anEntry.name,
		url: `${SEO_SITE.origin}${anEntry.path}`,
	})),
	mainEntity: {
		'@type': 'ItemList',
		numberOfItems: params.items.length,
		itemListElement: params.items.map((anEntry, idx) => ({
			'@type': 'ListItem',
			position: idx + 1,
			name: anEntry.name,
			url: `${SEO_SITE.origin}${anEntry.path}`,
		})),
	},
})
